// services/airtable.js

import axios from 'axios';
import { getClerkSessionToken } from '@/services/clerk';

const apiUrl = process.env.VUE_APP_NODE_ENV === 'production'
  ? process.env.VUE_APP_PROD_API_URL
  : process.env.VUE_APP_DEV_API_URL;

const airtableInstance = axios.create({
  baseURL: `${apiUrl.replace(/\/$/, '')}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

async function requestAirtable(config, { publicRequest = false } = {}) {
  const headers = { ...config.headers };

  if (!publicRequest) {
    const sessionToken = await getClerkSessionToken();

    if (!sessionToken) {
      throw new Error('You must be signed in to access Airtable data.');
    }

    headers.Authorization = `Bearer ${sessionToken}`;
  }

  return airtableInstance.request({
    ...config,
    headers,
  });
}

// Fetches records based on a table and a filter
export const fetchAllRecordsFromTable = async (tableName, filter = null) => {
  const records = [];
  const pageSize = 100;

  // console.log('tablename from fetchAllRecordsFromTable:', tableName);
  // console.log('filter from fetchAllRecordsFromTable:', filter);
  const fetchPage = async (offset) => {
    const response = await requestAirtable({
      method: 'get',
      url: `/airtable/${encodeURIComponent(tableName)}`,
      params: {
        filterByFormula: filter,
        pageSize: pageSize,
        offset: offset,
      },
    });

    records.push(...response.data.records);

    if (response.data.offset) {
      await fetchPage(response.data.offset);
    }
  };

  await fetchPage(null);
  return records;
};

// DONE Error handling
// Creates records
export async function createRecords(tableName, newRecords) {
  try {
    const formattedData = {
      records: newRecords.map(record => ({ fields: record })),
    };

    const response = await requestAirtable({
      method: 'post',
      url: `/airtable/${encodeURIComponent(tableName)}`,
      data: formattedData,
    });
    return response.data;

  } catch (error) {
    console.error(`Error creating records in table ${tableName}:`, error);
    throw error;
  }
}

// DONE Error handling
// Fetches records from a table
export async function fetchRecords(tableName, filter = null) {
  try {
    const response = await fetchAllRecordsFromTable(tableName, filter);
    return response; // don't change this to response.data, because fetchAllRecordsFromTable trims the response from Airtable before

  } catch (error) {
    console.error(`Error fetching records from table ${tableName}:`, error);
    throw error;

  }
}

// DONE Error handling
// Fetches related records
export async function fetchRelatedRecords(parentTableName, childTableName, recordId, relatedFieldName, { publicResource = null } = {}) {
  try {
    if (publicResource === 'quote') {
      const response = await requestAirtable({
        method: 'get',
        url: `/public/airtable/quote/${encodeURIComponent(recordId)}/jobs`,
      }, { publicRequest: true });

      return response.data.records;
    }

    // Step 1: Fetch the parent record and extract linked child record IDs
    const parentFilter = `RECORD_ID()='${recordId}'`;
    const parentRecord = await fetchAllRecordsFromTable(parentTableName, parentFilter);

    const relatedIds = parentRecord[0]?.fields[relatedFieldName] || [];

    // Step 2: Fetch the child records using the extracted IDs
    let childRecords = [];

    if (relatedIds.length > 0) {
      const batchSize = 50; // Adjust this value based on your needs
      for (let i = 0; i < relatedIds.length; i += batchSize) {
        const batchIds = relatedIds.slice(i, i + batchSize);
        const childFilter = `OR(${batchIds.map(id => `RECORD_ID()='${id}'`).join(',')})`;
        const batchRecords = await fetchAllRecordsFromTable(childTableName, childFilter);
        childRecords.push(...batchRecords);
      }
    }

    return childRecords;

  } catch (error) {
    console.error(`Error fetching related records from parent table ${parentTableName} to child table ${childTableName} with record ID: ${recordId}`, error);
    throw error;

  }
}

// DONE Error handling
// Creates a record
export async function createRecord(tableName, newRecord) {
  try {
    const formattedData = {
      records: [
        {
          fields: {
            ...newRecord,
          },
        },
      ],
    };

    const response = await requestAirtable({
      method: 'post',
      url: `/airtable/${encodeURIComponent(tableName)}`,
      data: formattedData,
    });
    return response.data;

  } catch (error) {
    console.error(`Error creating record in table ${tableName}:`, error);
    throw error;

  }
}

// DONE Error handling
// Fetches a record
export async function fetchRecord(tableName, recordId, { publicResource = null } = {}) {
  try {
    const url = publicResource
      ? `/public/airtable/${encodeURIComponent(publicResource)}/${encodeURIComponent(recordId)}`
      : `/airtable/${encodeURIComponent(tableName)}/${encodeURIComponent(recordId)}`;
    const response = await requestAirtable({
      method: 'get',
      url,
    }, { publicRequest: !!publicResource });
    return response.data;

  } catch (error) {
    console.error(`Error fetching record in table ${tableName}:`, error);
    throw error;

  }
}

// DONE Error handling
// Updates a record
export async function updateRecord(tableName, recordId, updatedFields, { publicResource = null } = {}) {
  try {
    const formattedData = publicResource
      ? { fields: updatedFields }
      : {
        records: [
          {
            id: recordId,
            fields: updatedFields,
          },
        ],
      };
    const url = publicResource
      ? `/public/airtable/${encodeURIComponent(publicResource)}/${encodeURIComponent(recordId)}`
      : `/airtable/${encodeURIComponent(tableName)}`;

    const response = await requestAirtable({
      method: 'patch',
      url,
      data: formattedData,
    }, { publicRequest: !!publicResource });
    return response.data;

  } catch (error) {
    console.error(`Error updating record in table ${tableName}:`, error);
    throw error;

  }
}

// DONE Error handling
// Deletes a record
export async function deleteRecord(tableName, recordId) {
  try {
    const response = await requestAirtable({
      method: 'delete',
      url: `/airtable/${encodeURIComponent(tableName)}/${encodeURIComponent(recordId)}`,
    });
    return response.data;

  } catch (error) {
    console.error(`Error deleting record in table ${tableName}:`, error);
    throw error;

  }
}
